/* eslint-disable max-len */
/**
 * BillingCyclesHeader Component
 * 
 * A reusable header component for billing cycle management forms.
 * Displays customer information, date selection, and reason fields.
 * 
 * @component
 * @example
 * ```tsx
 * <BillingCyclesHeader
 *   data={{
 *     customerNumber: "CUST001",
 *     contractNumber: "CONTRACT001",
 *     nextBcStartDate: "2024-01-01",
 *     reasonName: "Reason 1",
 *     comment: "Initial comment"
 *   }}
 *   onSave={(formData) => console.log(formData)}
 *   reasonOptions={[
 *     { value: "Reason 1", label: "Reason 1" },
 *     { value: "Reason 2", label: "Reason 2" }
 *   ]}
 * />
 * ```
 * 
 * @props
 * - data: BillingCyclesModalData - Initial form data
 * - onSave?: (formData: FormData) => void - Optional callback when save is clicked
 * - reasonOptions?: Array<{value: string, label: string}> - Optional custom reason options
 * - reasonType?: string - Optional custom reason type (defaults to "Dates Modified")
 * - readOnlyFields?: string[] - Optional array of field names to make read-only
 * 
 * @performance
 * - Optimized with useCallback for event handlers
 * - Minimal re-renders through controlled state management
 */

import { useState, useCallback, useEffect, useMemo } from 'react';

import { Input } from '@/components/evo/ui/input';
import { Label } from '@/components/evo/ui/label';
import { Textarea } from '@/components/evo/ui/textarea';
import { Button } from '@/components/evo/ui/button';
import { CustomDatePicker } from '@/components/evo/ui/custom-date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { BillingCyclesModalData } from './types';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { DATES_MODIFIED } from '@/config/reasonTypes.config';

interface BillingCyclesHeaderProps {
  data: BillingCyclesModalData;
  onSave?: (formData: FormData) => void;
  reasonOptions?: Array<{ value: string; label: string; code?: string }>;
  reasonType?: string;
  readOnlyFields?: string[];
  isSaving?: boolean;
}

interface FormData {
  customerNumber: string;
  contractNumber: string;
  nextBcStartDate: string;
  reasonType: string;
  reasonName: string;
  reasonCode: string;
  comment: string;
}

const DEFAULT_REASON_TYPE = 'Dates Modified';

export function BillingCyclesHeader({
  data,
  onSave,
  reasonOptions: propReasonOptions,
  reasonType = DEFAULT_REASON_TYPE,
  readOnlyFields = ['customerNumber', 'contractNumber', 'reasonType'],
  isSaving = false,
}: BillingCyclesHeaderProps) {
  const { reasonCodes, isLoading: isLoadingReasonCodes, getReasonCodes } = useGetReasonCodes();

  useEffect(() => {
    getReasonCodes(DATES_MODIFIED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reasonOptions = useMemo(() => {
    if (propReasonOptions) {
      return propReasonOptions;
    }
    return reasonCodes.map((rc) => ({
      value: rc.code,
      label: rc.name,
      code: rc.code,
    }));
  }, [propReasonOptions, reasonCodes]);
  // Form state - nextBcStartDate stored as YYYY-MM-DD string for CustomDatePicker
  const [customerNumber] = useState(data.customerNumber || '');
  const [contractNumber] = useState(data.contractNumber || '');
  const [nextBcStartDate, setNextBcStartDate] = useState<string>(
    data.nextBcStartDate ? data.nextBcStartDate.split('T')[0] : ''
  );
  const [isDateValid, setIsDateValid] = useState(true);
  const [reasonName, setReasonName] = useState(data.reasonName || '');
  const [comment, setComment] = useState(data.comment || '');

  // Optimized handlers with useCallback
  const handleDateChange = useCallback((date: string) => {
    setNextBcStartDate(date);
  }, []);

  const handleDateValidationChange = useCallback((valid: boolean) => {
    setIsDateValid(valid);
  }, []);

  const handleReasonNameChange = useCallback((value: string) => {
    setReasonName(value);
  }, []);

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  }, []);

  const handleSave = useCallback(() => {
    const selectedReason = reasonOptions.find((r) => r.value === reasonName);
    const formData: FormData = {
      customerNumber,
      contractNumber,
      nextBcStartDate,
      reasonType,
      reasonName: selectedReason?.label || reasonName,
      reasonCode: selectedReason?.code || reasonName,
      comment,
    };

    // Call optional save callback
    if (onSave) {
      onSave(formData);
    }
  }, [customerNumber, contractNumber, nextBcStartDate, reasonType, reasonName, reasonOptions, comment, onSave]);

  // Helper to check if field is read-only
  const isReadOnly = (fieldName: string) => readOnlyFields.includes(fieldName);

  // Validation: Disable save button if required fields are missing (same as BillingCadenceModal)
  const isSaveDisabled = !nextBcStartDate || !isDateValid || !reasonName || !comment.trim() || isSaving || isLoadingReasonCodes;

  return (
    <div className="grid gap-3 p-3 bg-muted/30 rounded-lg border">
      {/* Row 1: Customer Number and Contract Number */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="customer-number">Customer Number</Label>
          <Input
            id="customer-number"
            value={customerNumber}
            readOnly={isReadOnly('customerNumber')}
            className={isReadOnly('customerNumber') ? 'bg-muted cursor-not-allowed' : ''}
            placeholder="Enter customer number"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="contract-number">Contract Number</Label>
          <Input
            id="contract-number"
            value={contractNumber}
            readOnly={isReadOnly('contractNumber')}
            className={isReadOnly('contractNumber') ? 'bg-muted cursor-not-allowed' : ''}
            placeholder="Enter contract number"
          />
        </div>
      </div>

      {/* Row 2: Next BC Start Date and Reason Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="next-bc-start-date">
            New BC Start Date <span className="text-destructive">*</span>
          </Label>
          <CustomDatePicker
            value={nextBcStartDate}
            onChange={handleDateChange}
            onValidationChange={handleDateValidationChange}
            placeholder="Pick a date"
            allowManualInput={true}
            required={true}
            showValidationError={true}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reason-type">Reason Type</Label>
          <Input
            id="reason-type"
            value={reasonType}
            disabled={isReadOnly('reasonType')}
            className="bg-muted cursor-not-allowed"
          />
        </div>
      </div>

      {/* Row 3: Reason Name and Comment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="reason-name">
            Reason Name <span className="text-destructive">*</span>
          </Label>
          <Select value={reasonName} onValueChange={handleReasonNameChange}>
            <SelectTrigger id="reason-name">
              <SelectValue placeholder="Select reason" />
            </SelectTrigger>
            <SelectContent>
              {reasonOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="comment">
            Comment <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="comment"
            value={comment}
            onChange={handleCommentChange}
            placeholder="Enter short comments"
            className="resize-none"
            rows={1}
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={isSaveDisabled}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
